window.product_variations_html = {};
class ProductVariations extends HTMLElement {
  constructor() {
    super();
    this.sectionId = this.getAttribute('data-section-id');
    this.context = this.getAttribute('data-context');
    this.isQuickBuy = this.context === 'quick_buy';
    this.isMainProduct = this.context === 'main_product';
    this._onVariationClickListener = this.onVariationClickFunction.bind(this);
    this._onPopStateListener = this.onPopStateFunction.bind(this);
    this.abortController = new AbortController();
  }

  connectedCallback() {
    this.preloadAllProductVariations();
    this.sectionId = this.getAttribute('data-section-id');
    this.context = this.getAttribute('data-context');
    this.isQuickBuy = this.context === 'quick_buy';
    this.isMainProduct = this.context === 'main_product';
    this._onVariationClickListener = this.onVariationClickFunction.bind(this);
    this._onPopStateListener = this.onPopStateFunction.bind(this);
    this.addEventListener('click', this._onVariationClickListener, { signal: this.abortController.signal });
    if (this.isMainProduct) {
      window.addEventListener('popstate', this._onPopStateListener, { signal: this.abortController.signal });
    }
  }

  disconnectedCallback() {
    this.abortController.abort();
  }

  async onVariationClickFunction(e) {
    const el = e.target.closest('a.thumbnail-swatch, a.block-swatch');
    if (!el) return;

    e.preventDefault();
    el.classList.add('processing-spinner');
    const pathname = `/products/${el.dataset.handle}`;

    try {
      await this.renderProductPage(pathname);
      if (this.isMainProduct) {
        this.updatePageUrl(pathname);
      }
    } catch (err) {
      window.location.href = pathname;
    }
  }

  onPopStateFunction() {
    const pathname = window.location.pathname;
    if (window.product_variations_html[pathname]) {
      this.renderProductPage(pathname);
    } else {
      window.location.reload();
    }
  }

  preloadAllProductVariations() {
    const productVariations = this.querySelectorAll('a');
    productVariations.forEach((a) => this.loadProductVariation(a.getAttribute('href')).catch(() => {}));
  }

  async loadProductVariation(href) {
    if (window.product_variations_html[href]) return;

    const url = `${href}?sections=${this.sectionId}&type=${this.context}&pdp_ajax=true`;
    const response = await fetch(url);
    if (!response.ok) throw new Error(`Failed to load ${href}: ${response.status}`);
    const data = await response.json();
    if (!data[this.sectionId]) throw new Error(`Section ${this.sectionId} not found in response`);
    window.product_variations_html[href] = data[this.sectionId];
  }

  updatePageUrl(pathname) {
    window.history.pushState(null, null, pathname);
  }

  async renderProductPage(pathname) {
    if (!window.product_variations_html[pathname]) {
      await this.loadProductVariation(pathname);
    }

    const parser = new DOMParser();
    const doc = parser.parseFromString(window.product_variations_html[pathname], 'text/html');

    const selectors = {
      wrapperContent: this.isMainProduct ? '.shopify-section--main-product' : '.quick-buy-modal__content',
      quickBuyTemplateId: 'quick-buy-content',
    };

    const oldContent = this.closest(selectors.wrapperContent);
    let newContent;
    if (this.isMainProduct) {
      newContent = doc.querySelector(selectors.wrapperContent);
      const stickyBar = oldContent.querySelector('product-sticky-bar');
      if (stickyBar && stickyBar.classList.contains('is-visible')) {
        newContent.querySelector('product-sticky-bar').classList.add('is-visible');
      }
    } else {
      newContent = doc
        .getElementById(selectors.quickBuyTemplateId)
        .content.cloneNode(true)
        .querySelector(selectors.wrapperContent);
    }

    // Preload images before swapping content to avoid blank flashes
    const newImages = newContent.querySelectorAll('img[src]');
    await Promise.all([...newImages].map(img => {
      return new Promise(resolve => {
        const preload = new Image();
        preload.onload = preload.onerror = resolve;
        preload.src = img.src;
      });
    }));

    // Lock height and fade out so the DOM swap doesn't cause a visible layout reflow
    const prevHeight = oldContent.offsetHeight;
    oldContent.style.minHeight = `${prevHeight}px`;
    oldContent.style.transition = 'opacity 150ms ease';
    oldContent.style.opacity = '0';

    await new Promise(resolve => setTimeout(resolve, 150));

    oldContent.innerHTML = newContent.innerHTML;

    // Fade in the new content
    oldContent.style.opacity = '0';
    requestAnimationFrame(() => {
      oldContent.style.transition = 'opacity 200ms ease';
      oldContent.style.opacity = '1';
      setTimeout(() => {
        oldContent.style.minHeight = '';
        oldContent.style.transition = '';
        oldContent.style.opacity = '';
      }, 200);
    });

    this.preloadAllProductVariations();

    if (this.isMainProduct) {
      document.title = document.querySelector('.product-info  h1.product-title')?.innerText || document.title;
      const appSections = document.querySelectorAll('.shopify-section--apps');
      appSections.forEach((section) => (section.style.minHeight = `${section.offsetHeight}px`));

      await this.renderAdditionalShopifySections(pathname);
      this.yotpoInitWidgets();
    }
  }

  yotpoInitWidgets() {
    yotpoWidgetsContainer?.initWidgets();

    setTimeout(() => {
      const appSections = document.querySelectorAll('.shopify-section--apps');
      appSections.forEach((section) => (section.style.minHeight = ''));
    }, 1000);
  }

  async renderAdditionalShopifySections(pathname) {
    const excludedSections = ['.shopify-section--main-product', '.shopify-section--custom-liquid'];
    const sections = Array.from(document.querySelectorAll(`main .shopify-section:not(${excludedSections.join(',')})`));

    // Shopify only allows to fetch 5 sections at a time
    const sectionIds = sections.map((section) => section.id.replace('shopify-section-', '')).slice(0, 5);

    const url = `${pathname}?sections=${sectionIds.join(',')}`;
    const response = await fetch(url);
    const data = await response.json();

    sectionIds.forEach((sectionId) => {
      const newContent = new DOMParser().parseFromString(data[sectionId], 'text/html');
      const oldContent = document.getElementById(`shopify-section-${sectionId}`);
      oldContent.innerHTML = newContent.querySelector('.shopify-section').innerHTML;
    });
  }
}

customElements.define('product-variations', ProductVariations);

window.quickShopHtmls = {};
class CardQuickShop extends HTMLElement {
  constructor() {
    super();
    this.isRendered = false;
    this.productSectionId = null;
    this.observer = null;
    this.abortController = new AbortController();
  }

  connectedCallback() {
    this.productHandle = this.getAttribute('data-product-handle');
    this.productCard = this.closest('product-card');

    if (!this.productHandle || !this.productCard) {
      return;
    }

    this.observeProductCard();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.abortController.abort();
  }

  initProductCardListeners() {
    this.productCard.addEventListener('mouseenter', this.handleMouseEnter.bind(this), {
      signal: this.abortController.signal,
    });
    this.productCard.addEventListener('mouseleave', this.handleMouseLeave.bind(this), {
      signal: this.abortController.signal,
    });
    this.productCard.addEventListener('click', this.handleProductCardClick.bind(this), {
      signal: this.abortController.signal,
    });
  }

  handleMouseEnter() {
    const height = this.productCard.offsetHeight;
    this.productCard.style.minHeight = `${height}px`;
    this.productCard.classList.add('product-card--hovered');

    this.productCard.classList.add('product-card--quick-shop-rendered');

    if (!this.isRendered) {
      this.showLoadingSkeleton();
    }

    this.renderProductQuickShop(this.productHandle);
    this.enableFixScrollCarousel(true);
  }

  showLoadingSkeleton() {
    this.innerHTML = `
      <div class="product-card__figure" style="opacity: 0.5;">
        <div style="background: #f0f0f0; aspect-ratio: 1/1; animation: pulse 1.5s ease-in-out infinite;"></div>
      </div>
      <div class="product-card__info gap-y-1 sm:gap-y-2" style="opacity: 0.5;">
        <div style="background: #f0f0f0; height: 20px; width: 80%; margin-bottom: 8px; animation: pulse 1.5s ease-in-out infinite;"></div>
        <div style="background: #f0f0f0; height: 16px; width: 40%; animation: pulse 1.5s ease-in-out infinite;"></div>
      </div>
      <style>
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      </style>
    `;
  }

  handleMouseLeave() {
    this.productCard.style.minHeight = '';
    this.productCard.classList.remove('product-card--hovered');
    this.innerHTML = '';
    this.isRendered = false;

    this.enableFixScrollCarousel(false);
  }

  enableFixScrollCarousel(apply) {
    const scrollCarousel = this.productCard.closest('scroll-carousel');
    const productList = this.productCard.closest('product-list');
    if (scrollCarousel && productList) {
      scrollCarousel.style.position = apply ? 'relative' : '';
      scrollCarousel.style.left = apply ? `-${scrollCarousel.scrollLeft}px` : '';
      scrollCarousel.style.overflow = apply ? 'visible' : '';
      productList.style.overflowX = apply ? 'clip' : '';
    }
  }

  async handleProductCardClick(e) {
    const el = e.target.closest('a.thumbnail-swatch');
    if (!el) return;

    e.preventDefault();
    this.querySelector('.thumbnail-swatch.is-selected')?.classList.remove('is-selected');
    el.classList.add('processing-spinner', 'is-selected');
    await this.renderProductQuickShop(el.dataset.handle);
    el.classList.remove('processing-spinner');
    this.loadProductVariations();
  }

  loadProductVariations() {
    const variationsHandle = Array.from(this.querySelectorAll('.product_variations .thumbnail-swatch')).map(
      (el) => el.dataset.handle
    );
    variationsHandle.forEach((handle) => this.fetchProductQuickShop(handle));
  }

  observeProductCard() {
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.25,
    };

    const observerCallback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          this.fetchProductQuickShop(this.productHandle);
          this.initProductCardListeners();
          this.observer.disconnect();
        }
      });
    };

    this.observer = new IntersectionObserver(observerCallback, observerOptions);
    this.observer.observe(this.productCard);
  }

  async fetchProductQuickShop(productHandle, returnHtmlParser = false) {
    if (window.quickShopHtmls[productHandle]) {
      return returnHtmlParser
        ? new DOMParser()
            .parseFromString(window.quickShopHtmls[productHandle], 'text/html')
            .querySelector('product-card-quick-shop')
        : null;
    }

    const url = new URL(`${Shopify.routes.root}products/${productHandle}`, window.location.origin);
    url.searchParams.append('type', 'product_card_quick_shop');
    url.searchParams.append('pdp_ajax', 'true');
    if (this.productSectionId) {
      url.searchParams.append('section_id', this.productSectionId);
    }

    const response = await fetch(url);
    const responseHtml = await response.text();
    const newContent = new DOMParser().parseFromString(responseHtml, 'text/html');
    const quickShopElement = newContent.querySelector('product-card-quick-shop');

    if (quickShopElement) {
      window.quickShopHtmls[productHandle] = quickShopElement.outerHTML;
    }

    return returnHtmlParser ? quickShopElement : null;
  }

  async renderProductQuickShop(productHandle) {
    const quickShopHtml = await this.fetchProductQuickShop(productHandle, true);

    if (!quickShopHtml) {
      return;
    }

    this.productSectionId = quickShopHtml.getAttribute('data-section-id');

    if (this.isRendered) {
      const elementsToRerender = [
        '.product-card__figure',
        '.product-card__info',
        '.product-card__variant-picker-and-buy-buttons',
      ];

      elementsToRerender.forEach((el) => {
        const newElement = quickShopHtml.querySelector(el);
        const existingElement = this.querySelector(el);
        if (newElement && existingElement) {
          existingElement.innerHTML = newElement.innerHTML;
        }
      });
    } else {
      this.innerHTML = quickShopHtml.innerHTML;
      this.isRendered = true;
    }

    this.loadProductVariations();
    this.initVariationImageLoaders();

    // Initialize Yotpo after quick shop is rendered
    setTimeout(() => {
      if (typeof yotpo !== 'undefined' && yotpo.refreshWidgets) {
        yotpo.refreshWidgets();
      }
    }, 100);
  }

  initVariationImageLoaders() {
    const variationImages = this.querySelectorAll('.product_variations .thumbnail-swatch img');

    variationImages.forEach((img) => {
      const swatch = img.closest('.thumbnail-swatch');

      if (img.complete && img.naturalHeight !== 0) {
        swatch?.classList.add('image-loaded');
      } else {
        img.addEventListener('load', () => {
          swatch?.classList.add('image-loaded');
        }, { once: true });
        
        img.addEventListener('error', () => {
          swatch?.classList.add('image-loaded');
        }, { once: true });
      }
    });
  }
}

customElements.define('product-card-quick-shop', CardQuickShop);

function isVisibleInViewport(elm) {
  var rect = elm.getBoundingClientRect();
  var viewHeight = Math.max(document.documentElement.clientHeight, window.innerHeight);
  return !(rect.bottom < 0 || rect.top - viewHeight >= 0);
}

class ProductGalleryNextButton extends HTMLElement {
  constructor() {
    super();

    this.observer = null;
    this.abortController = new AbortController();
  }

  connectedCallback() {
    this.button = this.querySelector('button');
    this.gallery = this.closest('.product').querySelector('.product-gallery__image-list');
    this.galleryItems = this.gallery.querySelectorAll('.product-gallery__media');

    this.galleryIntersectionObserver();
    this.initListeners();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.abortController.abort();
  }

  initListeners() {
    this.button.addEventListener('click', this.scrollNextPosition.bind(this), {
      signal: this.abortController.signal,
    });
  }

  async scrollNextPosition(e) {
    e.preventDefault();
    const lastVisibleIndex = Array.from(this.galleryItems).reduce((lastIndex, item, index) => {
      return isVisibleInViewport(item) ? index : lastIndex;
    }, -1);

    if (lastVisibleIndex !== -1) {
      const nextIndex = Math.min(lastVisibleIndex + 1, this.galleryItems.length - 1);
      const nextItem = this.galleryItems[nextIndex];
      nextItem.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  galleryIntersectionObserver() {
    const observerOptions = {
      root: null,
      rootMargin: `-${window.innerHeight * 1.5}px 0px 0px 0px`,
      threshold: 0,
    };

    const observerCallback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          this.button.style.opacity = 1;
        } else {
          this.button.style.opacity = 0;
        }
      });
    };

    this.observer = new IntersectionObserver(observerCallback, observerOptions);
    this.observer.observe(this.gallery);
  }
}

customElements.define('product-gallery-next-button', ProductGalleryNextButton);
